import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { withRouter } from "react-router-dom";
import { ProgressBar } from "react-bootstrap";
import { marked } from "marked";

import { Images } from "../../../../../assets/images";
import {
  messages,
  sharedConstants,
  shutterbDashboardConstants,
} from "../../../../../constants";
import { customToast, convertBytesToMbs, convertHeicToJpeg } from "../../../../../shared/utility";
import VideoModal from "../../../../../shared/components/Modal/VideoModal";
import CustomModal from "../../../../../shared/components/Modal/CustomModal";
import EventDetailRow from "../../../../../shared/components/Events/EventDetailRow";
import EventActionCard from "../../../../../shared/components/Events/EventActionCard";
import UploadEventActionCard from "../../../../../shared/components/Events/UploadEventActionCard";
import RecapUploadPreview from "../../../../../shared/components/RecapUploadPreview/RecapUploadPreview";
import { shutterbDashboardActions } from "../../../../../redux/actions";

const ReelGallery = (props) => {
  const [files, setFiles] = useState([]);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reelModalOpen, setReelModalOpen] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState(0);

  const handleDelete = async () => {
    setFiles([]);
  };

  const handleFileInputChange = async (event) => {
    if (files.length > 0) {
      customToast(messages.FILE_ALREADY_SELECTED, toast.TYPE.ERROR);
      return;
    }
    const selectedFiles = event;
    // throwing error in case of image selection
    let isImg = Array.from(selectedFiles).filter((file) =>
      file.type.includes("image")
    );
    if (isImg.length > 0) {
      customToast("Only videos are acceptable for Reel.", toast.TYPE.ERROR);
      return null;
    }
    const convertedFiles = await Promise.all(
      Array.from(selectedFiles).map(async (file) => {
        const fileExtension = file.name.split(".").pop().toLowerCase();

        if (!file.type.includes("image") && !file.type.includes("video")) {
          customToast("File format not supported", toast.TYPE.ERROR);
          return null;
        }

        if (
          file.type.includes("image") &&
          (fileExtension === "heic" || fileExtension === "heif")
        ) {
          // Convert HEIC image to JPEG
          const convertedFile = await convertHeicToJpeg(file);
          return {
            file: convertedFile,
            url: URL.createObjectURL(convertedFile),
            selected: false,
          };
        } else {
          return {
            file,
            url: URL.createObjectURL(file),
            selected: false,
          };
        }
      })
    );

    // Filter out any null entries from unsupported formats
    const validFiles = convertedFiles.filter((file) => file !== null);
    setFiles(validFiles);
  };

  const handleUploadReels = () => {
    let formdata = new FormData();
    const filesToSend = files.filter((file) => !file.id);
    // size limit: total payload size limit
    if (
      convertBytesToMbs(
        filesToSend.reduce((accomulator, currentValue) => {
          return accomulator + currentValue?.file?.size ?? 0;
        }, 0)
      ) > process.env.REACT_APP_GALLERY_UPLOAD_PAYLOAD_LIMIT
    ) {
      customToast(
        `Upto ${process.env.REACT_APP_GALLERY_UPLOAD_PAYLOAD_LIMIT} MBs are allowed in an attempt`,
        toast.TYPE.ERROR
      );
      return null;
    }
    // size limit: each file size limit
    let fileWithInvalidSizeArr = filesToSend.filter((file) => {
      return (
        file.file.size / sharedConstants.KB_TO_MB_BYTES >=
        process.env.REACT_APP_GALLERY_UPLOAD_FILE_SIZE_LIMIT
      );
    });
    if (fileWithInvalidSizeArr.length > 0) {
      customToast(
        `Each File size should be under ${process.env.REACT_APP_GALLERY_UPLOAD_FILE_SIZE_LIMIT}MB.`,
        toast.TYPE.ERROR
      );
      props.diActivateOverlay();
      return null;
    }
    if (filesToSend.length === 0) {
      customToast(messages.RECAP_FILE_IS_REQUIRED, toast.TYPE.ERROR);
      return;
    }
    filesToSend.forEach((file) => {
      formdata.append("file", file.file);
    });
    if (props.eventId && !props.isCombinedEventsReel) {
      formdata.append("eventId", props.eventId);
    }
    formdata.append("orderItemId", props.orderItemId);
    formdata.append("eventGroupId", props.eventGroupId);
    formdata.append("SbUserId", props.shutterBId);
    formdata.append(
      "ContentType",
      shutterbDashboardConstants.CONTENT_TYPE_REEL
    );
    if (filesToSend.length > 0) {
      const options = (progressEvent) => {
        const { loaded, total } = progressEvent;
        let percent = ((loaded * 100) / total).toFixed(2);
        if (percent <= 100) {
          setUploadPercentage(percent);
        }
      };
      props.addReelsToGallery(formdata, options, onSuccess);
    }
  };

  useEffect(() => {
    setUploadPercentage(0);
    // eslint-disable-next-line
  }, [props.galleryData]);

  useEffect(() => {
    if (props.isUpdated) {
      setFiles([]);
      setUploadPercentage(0);
      props.closeAllAccordions();
      props.setIsUpdated(false);
    }
    // eslint-disable-next-line
  }, [props.isUpdated]);

  const handleOpenModal = (file) => {
    if (file && file.url) {
      setMediaUrl(file.url);
      setIsModalOpen(true);
    }
  };

  const onSuccess = (flag) => {
    if (flag) {
      props.fetchEvents(props.pageNumber);
    } else {
      setUploadPercentage(0);
    }
  };

  const getFormattedHtml = (text = "") => {
    // Simple check if text contains HTML tags
    const isHtml = /<\/?[a-z][\s\S]*>/i.test(text);

    return {
      __html: isHtml ? text : marked.parse(text),
    };
  };

  return (
    <>
      <div className="col-12 px-0">
        <div className="d-flex flex-wrap">
          <EventActionCard
            classes="mh-100px"
            iconSrc={Images.DownloadAssets}
            iconAlt="event-download-assets"
            title="download assets"
            isDisabled={!props.isAllowDownload}
            disabledIcon={Images.DownloadAssetsDisabled}
            onClick={() =>
              props.downloadAssets(props.orderItemId, props.sbUserName)
            }
          />
          <UploadEventActionCard
            classes="mh-100px"
            iconSrc={Images.VideoPosting}
            iconAlt="video-posting"
            disabledIcon={Images.VideoPosting}
            disabledAlt="video-posting"
            title="upload recaps"
            uploadPercentage={uploadPercentage}
            multiple={false}
            onFilesSelected={(files) => handleFileInputChange(files)}
            disabled={files.length > 0}
          />
          <EventActionCard
            classes="mh-100px"
            iconSrc={Images.Reel}
            iconAlt="recap-details-icon"
            onClick={() => {
              props.getReelDetails(props.orderItemId);
              setReelModalOpen((prev) => !prev);
            }}
            title="view recap details"
          />
        </div>
      </div>

      <div className="col-lg-12 px-0">
        {uploadPercentage > 0 && (
          <ProgressBar
            className="my-2"
            now={uploadPercentage}
            active
            label={`${Number(uploadPercentage).toFixed(0)}%`}
          />
        )}
        {files.length > 0 && (
          <div className="image-preview-container">
            <RecapUploadPreview
              id={files.length > 0 ? files[0] : null}
              image={files.length > 0 ? files[0] : null}
              handleOpenModal={handleOpenModal}
              showCheckBox={false}
              showDeleteIcon
              onDelete={handleDelete}
            />
          </div>
        )}
        <div className="col-12 d-flex pt-4 pl-0">
          <button
            className="button-common button-fill"
            disabled={uploadPercentage > 0}
            onClick={handleUploadReels}
          >
            submit
          </button>
        </div>
      </div>

      <CustomModal
        show={reelModalOpen}
        onClose={() => setReelModalOpen(false)}
        title="recap brief"
      >
        <div className="view-details-modal-content">
          {(() => {
            const details = props.reelDetails?.reelDetails?.[0] || {};

            const logoUrl = details.logoUrl ? (
              <a
                href={`${process.env.REACT_APP_S3_BASE_URL_EVENTS_GALLERY}/${details.logoUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "underline" }}
              >
                {`${process.env.REACT_APP_S3_BASE_URL_EVENTS_GALLERY}/${details.logoUrl}`}
              </a>
            ) : (
              details.logoUrl
            );

            const rows = [
              { label: "logo url:", value: logoUrl },
              { label: "orientation:", value: details.orientation },
              { label: "recap type:", value: details.reelMode },
              {
                label: "editing brief:",
                value: details.description,
                isEditBrief: true,
              },
            ];

            return rows.map((row, idx) =>
              row?.isEditBrief ? (
                <div className="row">
                  <div className="col-auto col-md-4 col-lg-3 col-xl-2 pr-0">
                    {row.label && (
                      <span className="p-0 event-detail-heading">
                        {row.label}
                      </span>
                    )}
                  </div>
                  <div className="col-auto col-md-8 col-lg-9 col-xl-10">
                    {row.value && (
                      <span
                        className="p-0 event-detail-info"
                        dangerouslySetInnerHTML={getFormattedHtml(row.value)}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <EventDetailRow key={idx} label={row.label} value={row.value} />
              )
            );
          })()}
        </div>
      </CustomModal>

      <VideoModal
        videoUrl={mediaUrl}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  isUpdated: state.shutterbDashboard.isUpdated,
  galleryData: state.shutterbDashboard.galleryData,
  reelDetails: state.shutterbDashboard.reelDetails,
  shutterBId: state.shutterbAuthentication.sbUserId,
  sbUserName: state.shutterbAuthentication.sbUserName,
  isImageDeleted: state.shutterbDashboard.isImageDeleted,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getReelDetails: (eventId) =>
    dispatch(shutterbDashboardActions.getReelDetails(eventId)),
  addReelsToGallery: (data, options, onSuccess) =>
    dispatch(
      shutterbDashboardActions.addReelsToGallery(data, options, onSuccess)
    ),
  setIsUpdated: (data) => dispatch(shutterbDashboardActions.setIsUpdated(data)),
  downloadAssets: (orderItemId, sbUserName) =>
    dispatch(shutterbDashboardActions.downloadAssets(orderItemId, sbUserName)),
  activateSpinner: () => dispatch({ type: "SPINNER_ACTIVATE", data: null }),
  diActivateSpinner: () => dispatch({ type: "SPINNER_DIACTIVATE", data: null }),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ReelGallery));
